import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Search, MessageCircle } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23FAQs = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('wedding');
    const [openFAQ, setOpenFAQ] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const tabs = [
        { value: 'wedding', label: 'Wedding Orders' },
        { value: 'festive', label: 'Festive Collections' },
        { value: 'sizing', label: 'Custom Sizing' },
        { value: 'shipping', label: 'Shipping' },
        { value: 'returns', label: 'Returns' },
    ];

    const faqs = {
        wedding: [
            {
                q: 'How far in advance should I order my bridal lehenga?',
                a: 'We recommend placing your bridal order at least 8-10 weeks before your wedding date. This allows adequate time for custom sizing, any alterations, and a comfortable delivery window. For our express service, a minimum of 4 weeks is required.',
            },
            {
                q: 'Can I customize the color and embroidery of my bridal outfit?',
                a: 'Absolutely! We offer full customization for bridal wear including fabric color, embroidery patterns (zardozi, thread work, sequin), blouse design, and dupatta style. Please contact us via WhatsApp for a bridal consultation where we can discuss your vision in detail.',
            },
            {
                q: 'Do you offer bridal packages for the entire wedding trousseau?',
                a: 'Yes, we offer curated bridal packages that include outfits for mehendi, sangeet, wedding ceremony, and reception. Package deals come with a 15-20% discount compared to individual purchases. Contact our bridal team for a personalized consultation.',
            },
            {
                q: 'Can I see fabric swatches before ordering my bridal outfit?',
                a: 'Yes! We offer a swatch service for bridal orders. Request up to 5 fabric swatches for a nominal fee of \u20B9199 (refundable on order placement). Swatches are delivered within 3-5 business days.',
            },
            {
                q: 'What if my wedding date changes after I have placed an order?',
                a: 'We understand that plans can change. If your wedding date changes, please notify us immediately. We will adjust the production and delivery timeline accordingly. For postponements, we can hold your order at no extra charge for up to 3 months.',
            },
        ],
        festive: [
            {
                q: 'When do new festive collections launch?',
                a: 'We launch seasonal collections approximately 6-8 weeks before major festivals. Our Diwali collection typically drops in September, Eid collections before Ramadan, and Navratri/Durga Puja collections in August. Sign up for our newsletter to get early access and exclusive previews.',
            },
            {
                q: 'Are the festive pieces limited edition?',
                a: 'Many of our festive pieces are produced in limited quantities to maintain exclusivity. Once a design sells out, it is typically not restocked. We recommend ordering early to secure your preferred piece, especially for popular designs.',
            },
            {
                q: 'Can I wear the festive outfits for different occasions?',
                a: 'Absolutely! While our pieces are designed with specific occasions in mind, many are versatile enough for multiple events. A sangeet anarkali can double as a Diwali outfit, and a festive saree works beautifully for pujas, parties, and formal gatherings.',
            },
            {
                q: 'Do you offer matching outfits for couples or families?',
                a: 'Yes! We offer coordinated couple sets and family matching options for festivals and celebrations. Our "Celebration Coordinates" line includes matching color palettes and complementary designs for the entire family.',
            },
        ],
        sizing: [
            {
                q: 'How do I determine my correct size?',
                a: 'We provide a detailed size guide on each product page with measurements in both inches and centimeters. For the most accurate fit, we recommend taking professional measurements. You can also share your measurements via WhatsApp, and our sizing experts will recommend the perfect size.',
            },
            {
                q: 'What is included in a custom-sized order?',
                a: 'Custom sizing includes adjustments to bust, waist, hip, and length measurements based on your specific dimensions. For bridal lehengas, we also accommodate blouse customization including neckline, sleeve length, and back design preferences.',
            },
            {
                q: 'How long does a custom-sized order take?',
                a: 'Custom-sized orders typically take 2-3 weeks for standard pieces and 4-8 weeks for heavily embroidered bridal wear. You will receive progress updates via WhatsApp, including photos of your outfit during the making process.',
            },
            {
                q: 'What if the custom-sized outfit does not fit perfectly?',
                a: 'While rare with our precision sizing process, if your custom outfit needs minor adjustments, we offer one free alteration within 7 days of delivery. For significant sizing issues (our error), we will remake the garment at no additional cost.',
            },
            {
                q: 'Can I provide my own measurements for an unstitched blouse?',
                a: 'Yes! You can provide your blouse measurements when placing your order. We include a measurement form where you can enter bust, shoulder width, arm circumference, blouse length, and preferred neckline style.',
            },
        ],
        shipping: [
            {
                q: 'How long does standard delivery take?',
                a: 'Standard delivery within India takes 7-10 business days from the date of dispatch. Orders to metro cities (Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad) typically arrive within 5-7 days. Rural areas may take an additional 2-3 days.',
            },
            {
                q: 'Do you offer express delivery for urgent orders?',
                a: 'Yes! Our express delivery service guarantees delivery within 3-5 business days for an additional \u20B9499. For wedding orders with tight timelines, we also offer a premium same-week delivery option. Contact us for availability in your area.',
            },
            {
                q: 'Do you ship internationally?',
                a: 'Yes, we ship to over 15 countries including USA, UK, UAE, Canada, Australia, Singapore, and more. International shipping takes 10-15 business days. Customs duties and taxes are the responsibility of the buyer and are not included in our pricing.',
            },
            {
                q: 'How can I track my order?',
                a: 'Once your order is dispatched, you will receive a tracking number via email and SMS. You can track your shipment in real-time through the courier partner\'s website or through your account dashboard under "My Orders."',
            },
            {
                q: 'Is my package insured during transit?',
                a: 'Yes, all domestic shipments above \u20B95,000 are fully insured against damage or loss during transit at no extra cost. For international orders, we offer optional shipping insurance at checkout for 2% of the order value.',
            },
        ],
        returns: [
            {
                q: 'What is your return policy?',
                a: 'We accept returns on ready-to-ship products within 7 days of delivery. Items must be unused, unwashed, and in their original packaging with all tags intact. Custom-made and bridal pieces are non-returnable unless defective.',
            },
            {
                q: 'How do I initiate a return?',
                a: 'You can initiate a return through your account dashboard under "My Orders" or by contacting us via WhatsApp (+91 98765 43210) or email (returns@utsav.com). Please have your order ID ready. Our team will process your request within 24 hours.',
            },
            {
                q: 'When will I receive my refund?',
                a: 'Once we receive and inspect the returned item, refunds are processed within 7-10 business days. The refund will be credited to your original payment method. For COD orders, we will process a bank transfer — please provide your bank details when initiating the return.',
            },
            {
                q: 'Can I exchange for a different size?',
                a: 'Yes! Size exchanges are free of charge for ready-to-ship items. Simply initiate an exchange request, and we will ship the new size once we receive the original item. If you need the new size urgently, we can ship it immediately and arrange a pickup for the original.',
            },
            {
                q: 'What if I receive a damaged or defective item?',
                a: 'We sincerely apologize if you receive a damaged item. Please contact us within 48 hours of delivery with photos of the damage. We will arrange an immediate replacement or full refund, including return shipping costs, at no charge to you.',
            },
        ],
    };

    const toggleFAQ = (idx) => {
        setOpenFAQ(openFAQ === idx ? null : idx);
    };

    const currentFAQs = faqs[activeTab] || [];
    const filteredFAQs = searchQuery
        ? Object.values(faqs).flat().filter(
            faq => faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   faq.a.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : currentFAQs;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-faq-tabs {
                    display: flex;
                    gap: 8px;
                    overflow-x: auto;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                    padding-bottom: 4px;
                }
                .utsav-faq-tabs::-webkit-scrollbar {
                    display: none;
                }
                .utsav-faq-item {
                    transition: all 0.3s ease;
                }
                .utsav-faq-answer {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.4s ease, padding 0.3s ease;
                }
                .utsav-faq-answer.open {
                    max-height: 500px;
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, #1B3A2D 0%, #2D5A45 100%)',
                    textAlign: 'center',
                    padding: '130px 24px 60px',
                }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h1 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(28px, 4vw, 42px)',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            margin: '0 0 12px 0',
                        }}>
                            Frequently Asked Questions
                        </h1>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '16px',
                            color: 'rgba(255,255,255,0.8)',
                            margin: '0 0 28px 0',
                            lineHeight: 1.6,
                        }}>
                            Everything you need to know about our festive collections, ordering, and delivery
                        </p>

                        {/* Search */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '12px',
                            padding: '0 16px',
                            maxWidth: '480px',
                            margin: '0 auto',
                        }}>
                            <Search size={18} color="rgba(255,255,255,0.6)" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search FAQs..."
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    backgroundColor: 'transparent',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#FFFFFF',
                                    padding: '14px 0',
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '40px 24px 80px',
                }}>
                    {/* Tabs */}
                    {!searchQuery && (
                        <div className="utsav-faq-tabs" style={{ marginBottom: '32px' }}>
                            {tabs.map(tab => (
                                <button
                                    key={tab.value}
                                    onClick={() => {
                                        setActiveTab(tab.value);
                                        setOpenFAQ(null);
                                    }}
                                    style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        padding: '10px 20px',
                                        backgroundColor: activeTab === tab.value ? '#C5974B' : 'transparent',
                                        color: activeTab === tab.value ? '#FFFFFF' : '#1B3A2D',
                                        border: activeTab === tab.value ? '1px solid #C5974B' : '1px solid #E8E0D4',
                                        borderRadius: '24px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== tab.value) {
                                            e.currentTarget.style.borderColor = '#C5974B';
                                            e.currentTarget.style.color = '#C5974B';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== tab.value) {
                                            e.currentTarget.style.borderColor = '#E8E0D4';
                                            e.currentTarget.style.color = '#1B3A2D';
                                        }
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {searchQuery && (
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '14px',
                            color: '#5A5A5A',
                            margin: '0 0 24px 0',
                        }}>
                            Showing {filteredFAQs.length} result{filteredFAQs.length !== 1 ? 's' : ''} for "{searchQuery}"
                        </p>
                    )}

                    {/* FAQ Accordion */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredFAQs.map((faq, idx) => (
                            <div
                                key={idx}
                                className="utsav-faq-item"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    border: openFAQ === idx ? '1px solid #C5974B' : '1px solid #E8E0D4',
                                    overflow: 'hidden',
                                    boxShadow: openFAQ === idx
                                        ? '0 4px 16px rgba(197, 151, 75, 0.12)'
                                        : '0 1px 4px rgba(27, 58, 45, 0.04)',
                                }}
                            >
                                <button
                                    onClick={() => toggleFAQ(idx)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '20px 24px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        gap: '16px',
                                    }}
                                >
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: '#1B3A2D',
                                        lineHeight: 1.4,
                                        flex: 1,
                                    }}>
                                        {faq.q}
                                    </span>
                                    <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        backgroundColor: openFAQ === idx ? '#C5974B' : '#F8F3EC',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition: 'all 0.3s ease',
                                        transform: openFAQ === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}>
                                        <ChevronDown
                                            size={16}
                                            color={openFAQ === idx ? '#FFFFFF' : '#C5974B'}
                                        />
                                    </div>
                                </button>

                                <div
                                    className={`utsav-faq-answer ${openFAQ === idx ? 'open' : ''}`}
                                    style={{
                                        padding: openFAQ === idx ? '0 24px 20px' : '0 24px 0',
                                    }}
                                >
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                        lineHeight: 1.8,
                                        margin: 0,
                                        borderTop: '1px solid #F8F3EC',
                                        paddingTop: '16px',
                                    }}>
                                        {faq.a}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredFAQs.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '48px 24px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                        }}>
                            <p style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '15px',
                                color: '#5A5A5A',
                                margin: '0 0 16px 0',
                            }}>
                                No FAQs found for your search. Try a different query or browse by category.
                            </p>
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    padding: '10px 24px',
                                    backgroundColor: '#1B3A2D',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Clear Search
                            </button>
                        </div>
                    )}

                    {/* Still have questions */}
                    <div style={{
                        backgroundColor: '#1B3A2D',
                        borderRadius: '16px',
                        padding: '40px 32px',
                        marginTop: '48px',
                        textAlign: 'center',
                    }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(197, 151, 75, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}>
                            <MessageCircle size={24} color="#C5974B" />
                        </div>
                        <h3 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            margin: '0 0 8px 0',
                        }}>
                            Still Have Questions?
                        </h3>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: 'rgba(255,255,255,0.7)',
                            margin: '0 0 24px 0',
                            lineHeight: 1.6,
                        }}>
                            Our team is here to help with custom orders, sizing advice, and bridal consultations
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                        }}>
                            <a
                                href="https://wa.me/919876543210"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    padding: '12px 28px',
                                    backgroundColor: '#C5974B',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <MessageCircle size={16} />
                                WhatsApp Us
                            </a>
                            <button
                                onClick={() => navigate('/template23/contact')}
                                style={{
                                    padding: '12px 28px',
                                    backgroundColor: 'transparent',
                                    color: '#FFFFFF',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '10px',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Contact Form
                            </button>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23FAQs;
